import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import type {
  SetupStepCompletion,
  SetupStepId,
} from "Components/Dashboard/SetupChecklist/types";
import { useHasPrimaryAccount } from "Hooks/useHasPrimaryAccount";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { IdentityVerificationStatus } from "Infrastructure/Api/Api";

export type SetupChecklistSteps = Record<SetupStepId, SetupStepCompletion>;

type SetupChecklistState = {
  isReady: boolean;
  isComplete: boolean;
  steps: SetupChecklistSteps | undefined;
  completedCount: number;
  totalCount: number;
};

const TOTAL_STEPS_COUNT = 3;

const NOT_READY_STATE: SetupChecklistState = {
  isReady: false,
  isComplete: false,
  steps: undefined,
  completedCount: 0,
  totalCount: TOTAL_STEPS_COUNT,
};

/**
 * Derives the state of the customer "Finish your setup" checklist from the
 * same sources the individual dashboard nags use. `isReady` is false until
 * every step is determinable (queries settled without error) — the card
 * renders nothing until then. A region-less user (unknown market rail) simply
 * counts as having no primary account: the blocking region-selection modal
 * covers the dashboard for them anyway.
 */
export const useSetupChecklistSteps = (): SetupChecklistState => {
  const user = useLoggedInUser();
  const accountsQuery = useBankingAccounts();
  const { hasPrimaryAccount } = useHasPrimaryAccount();
  const verificationsQuery = useVerificationsQuery(user?.userID);
  const { isLiteTier, isLiteBecauseOfBank, isDocExpired } =
    useIsUserInMooneLite();

  const accounts = accountsQuery.data;
  const verification = verificationsQuery.data;

  if (
    accounts === undefined ||
    verification === undefined ||
    accountsQuery.isError ||
    verificationsQuery.isError
  ) {
    return NOT_READY_STATE;
  }

  const isVerified = verification.verificationLevel > 1;

  const getVerifyIdentityCompletion = (): SetupStepCompletion => {
    // Doc-expired users already completed onboarding; their re-upload nag is
    // owned by MooneLitePopup's expired variant, so the row counts as done.
    if (isVerified || isDocExpired) {
      return "completed";
    }

    // A Lite customer whose identity KYC is accepted only lacks the bank — the
    // wizard cannot help them, so the identity row must not send them back
    // into it (mirrors the MooneLitePopup / account-detail CTAs).
    if (isLiteBecauseOfBank) {
      return verification.identityVerificationStatus ===
        IdentityVerificationStatus.Verified
        ? "completed"
        : "inReview";
    }

    // IdentityVerificationStatus cannot distinguish "submitted, awaiting review"
    // from "did nothing yet" — merely connecting a bank flips it to
    // Processing/ToVerify. The tier is the reliable signal: the Lite -> FullMoone
    // upgrade only happens once onboarding verification completes, so a Lite user
    // still has the onboarding CTA ahead of them, while a FullMoone user who
    // isn't verified yet is waiting on manual review.
    if (isLiteTier) {
      return "incomplete";
    }

    return "inReview";
  };

  const steps: SetupChecklistSteps = {
    connectBank: accounts.items.length > 0 ? "completed" : "incomplete",
    setPrimaryAccount: hasPrimaryAccount ? "completed" : "incomplete",
    verifyIdentity: getVerifyIdentityCompletion(),
  };

  const completedCount = Object.values(steps).filter(
    (completion) => completion === "completed",
  ).length;

  return {
    isReady: true,
    isComplete: completedCount === TOTAL_STEPS_COUNT,
    steps,
    completedCount,
    totalCount: TOTAL_STEPS_COUNT,
  };
};
